import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from './api';
import Spinner from './Spinner';

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    return {
        notificationBell: {
            position: 'relative',
            cursor: 'pointer',
            padding: '8px',
            fontSize: '20px',
            color: isDark ? '#e0e0e0' : '#333',
        },
        badge: {
            position: 'absolute',
            top: '0',
            right: '0',
            backgroundColor: '#dc3545',
            color: 'white',
            borderRadius: '50%',
            width: '18px',
            height: '18px',
            fontSize: '11px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold',
        },
        dropdown: {
            position: 'absolute',
            top: '100%',
            right: '0',
            marginTop: '10px',
            width: '400px',
            maxHeight: '500px',
            backgroundColor: isDark ? '#1e1e1e' : '#fff',
            border: `1px solid ${isDark ? '#444' : '#ddd'}`,
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1000,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
        },
        header: {
            padding: '15px',
            borderBottom: `1px solid ${isDark ? '#444' : '#ddd'}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        headerTitle: {
            fontSize: '16px',
            fontWeight: 'bold',
            color: isDark ? '#e0e0e0' : '#333',
        },
        markAllRead: {
            fontSize: '12px',
            color: isDark ? '#6cb2f7' : '#007bff',
            cursor: 'pointer',
            textDecoration: 'none',
        },
        notificationList: {
            overflowY: 'auto',
            flex: 1,
        },
        notificationItem: {
            padding: '12px 15px',
            borderBottom: `1px solid ${isDark ? '#333' : '#eee'}`,
            cursor: 'pointer',
            transition: 'background-color 0.2s',
        },
        notificationItemUnread: {
            backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa',
        },
        notificationItemRead: {
            backgroundColor: 'transparent',
        },
        notificationTitle: {
            fontSize: '14px',
            fontWeight: 'bold',
            color: isDark ? '#e0e0e0' : '#333',
            marginBottom: '4px',
        },
        notificationMessage: {
            fontSize: '12px',
            color: isDark ? '#a0a0a0' : '#666',
            marginBottom: '4px',
        },
        notificationTime: {
            fontSize: '11px',
            color: isDark ? '#666' : '#999',
        },
        priorityBadge: {
            display: 'inline-block',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '10px',
            fontWeight: 'bold',
            marginLeft: '8px',
        },
        priorityCritical: { backgroundColor: '#dc3545', color: 'white' },
        priorityHigh: { backgroundColor: '#fd7e14', color: 'white' },
        priorityMedium: { backgroundColor: '#ffc107', color: '#333' },
        priorityLow: { backgroundColor: '#6c757d', color: 'white' },
        emptyState: {
            padding: '40px 20px',
            textAlign: 'center',
            color: isDark ? '#a0a0a0' : '#666',
        },
        footer: {
            padding: '10px 15px',
            borderTop: `1px solid ${isDark ? '#444' : '#ddd'}`,
            textAlign: 'center',
        },
        viewAll: {
            fontSize: '12px',
            color: isDark ? '#6cb2f7' : '#007bff',
            cursor: 'pointer',
            textDecoration: 'none',
        },
    };
};

function NotificationCenter({ theme }) {
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const styles = getStyles(theme);

    const fetchNotifications = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api.get('/notifications/?ordering=-created_at');
            setNotifications(response.data.results || response.data || []);
            
            // Get unread count
            const unreadResponse = await api.get('/notifications/unread_count/');
            setUnreadCount(unreadResponse.data.unread_count || 0);
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchNotifications();
        // Poll for new notifications every 30 seconds
        const interval = setInterval(fetchNotifications, 30000);
        return () => clearInterval(interval);
    }, [fetchNotifications]);

    const markAsRead = async (notificationId) => {
        try {
            await api.post(`/notifications/${notificationId}/mark_read/`);
            setNotifications(prev => prev.map(n => 
                n.id === notificationId ? { ...n, is_read: true, read_at: new Date().toISOString() } : n
            ));
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const markAllAsRead = async () => {
        try {
            await api.post('/notifications/mark_all_read/');
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            setUnreadCount(0);
        } catch (error) {
            console.error('Error marking all as read:', error);
        }
    };

    const handleNotificationClick = async (notification) => {
        // Fix: Await markAsRead before navigating to ensure the API call completes
        if (!notification.is_read) {
            await markAsRead(notification.id);
        }
        
        if (notification.action_url) {
            try {
                // Parse the URL to determine if it's external or internal
                const url = new URL(notification.action_url, window.location.origin);
                
                // Check if it's an external URL (different origin)
                const isExternal = url.origin !== window.location.origin;
                
                if (isExternal) {
                    // Bug Fix 2: External URL - Use window.location.href to navigate to different domain
                    window.location.href = notification.action_url;
                } else {
                    // Bug Fix 1: Internal URL - Use React Router navigate, preserving pathname, search, AND hash
                    const internalPath = url.pathname + url.search + url.hash;
                    navigate(internalPath);
                }
            } catch (error) {
                // If URL parsing fails, treat as relative path and use React Router
                // This handles cases where action_url is just a path like '/status-board?buno=123#section'
                if (notification.action_url.startsWith('/')) {
                    navigate(notification.action_url);
                } else {
                    // Fallback to window.location.href for malformed URLs
                    console.warn('Invalid action_url format:', notification.action_url);
                    window.location.href = notification.action_url;
                }
            }
        }
    };

    const getPriorityStyle = (priority) => {
        switch (priority) {
            case 'critical': return { ...styles.priorityBadge, ...styles.priorityCritical };
            case 'high': return { ...styles.priorityBadge, ...styles.priorityHigh };
            case 'medium': return { ...styles.priorityBadge, ...styles.priorityMedium };
            case 'low': return { ...styles.priorityBadge, ...styles.priorityLow };
            default: return { ...styles.priorityBadge, ...styles.priorityMedium };
        }
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString();
    };

    return (
        <div style={{ position: 'relative' }}>
            <div 
                style={styles.notificationBell}
                onClick={() => setIsOpen(!isOpen)}
                title="Notifications"
            >
                🔔
                {unreadCount > 0 && (
                    <span style={styles.badge}>
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </div>
            
            {isOpen && (
                <div style={styles.dropdown}>
                    <div style={styles.header}>
                        <span style={styles.headerTitle}>Notifications</span>
                        {unreadCount > 0 && (
                            <span 
                                style={styles.markAllRead}
                                onClick={markAllAsRead}
                            >
                                Mark all read
                            </span>
                        )}
                    </div>
                    
                    <div style={styles.notificationList}>
                        {loading ? (
                            <div style={styles.emptyState}>
                                <Spinner />
                            </div>
                        ) : notifications.length === 0 ? (
                            <div style={styles.emptyState}>
                                No notifications
                            </div>
                        ) : (
                            notifications.map(notification => (
                                <div
                                    key={notification.id}
                                    style={{
                                        ...styles.notificationItem,
                                        ...(notification.is_read 
                                            ? styles.notificationItemRead 
                                            : styles.notificationItemUnread)
                                    }}
                                    onClick={() => handleNotificationClick(notification)}
                                >
                                    <div style={styles.notificationTitle}>
                                        {notification.title}
                                        <span style={getPriorityStyle(notification.priority)}>
                                            {notification.priority}
                                        </span>
                                    </div>
                                    <div style={styles.notificationMessage}>
                                        {notification.message}
                                    </div>
                                    <div style={styles.notificationTime}>
                                        {formatTime(notification.created_at)}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                    
                    <div style={styles.footer}>
                        <span 
                            style={styles.viewAll}
                            onClick={() => {
                                setIsOpen(false);
                                navigate('/notifications');
                            }}
                        >
                            View all notifications
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
}

export default NotificationCenter;

